/**
 * DriverReturnTasks — shows packages the driver must RETURN to sender after a
 * customer cancelled post-pickup. Rendered with a distinct RED/AMBER colour so it
 * stands out on the driver screen. Driver accepts within the window, then confirms
 * the return with the sender's return PIN.
 */
import { useState, useEffect, useCallback } from "react";
import { Undo2, AlertTriangle, Loader2, CheckCircle2, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api from "../lib/api";

const Countdown = ({ deadline }) => {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      if (!deadline) return setLeft("");
      const ms = new Date(deadline).getTime() - Date.now();
      if (ms <= 0) return setLeft("window closed");
      const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
      setLeft(`${m}m ${s}s left to accept`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [deadline]);
  if (!left) return null;
  return <span className="inline-flex items-center gap-1 text-xs text-amber-300"><Clock className="h-3 w-3" />{left}</span>;
};

const DriverReturnTasks = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pins, setPins] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/driver/returns");
      setReturns(res.data?.returns || []);
    } catch {
      /* silent — driver may not have returns */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  const accept = async (pid) => {
    setBusy(pid);
    try {
      await api.post(`/driver/returns/${pid}/accept`);
      toast.success("Return accepted — take the package back to the sender");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept return");
    } finally {
      setBusy(null);
    }
  };

  const complete = async (pid) => {
    const pin = (pins[pid] || "").trim();
    if (!pin) return toast.error("Ask the sender for the return PIN");
    setBusy(pid);
    try {
      await api.post(`/driver/returns/${pid}/complete`, { pin });
      toast.success("Return completed. Sender and receiver notified.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incorrect return PIN");
    } finally {
      setBusy(null);
    }
  };

  if (loading || returns.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="driver-return-tasks">
      {returns.map((r) => (
        <div
          key={r.package_id}
          data-testid={`return-card-${r.package_id}`}
          className="rounded-xl border-2 border-red-500/70 bg-gradient-to-r from-red-500/20 to-amber-500/10 p-4 shadow-lg"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 shrink-0 rounded-lg bg-red-500/30 flex items-center justify-center">
              <Undo2 className="h-5 w-5 text-red-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <span className="text-red-300 font-bold text-sm">RETURN TO SENDER</span>
              </div>
              <p className="text-white font-semibold mt-1">{r.tracking_id}</p>
              <p className="text-slate-300 text-xs">
                Cancelled by customer{r.sender_name ? ` — return to ${r.sender_name}` : ""}
                {r.sender_phone ? ` · ${r.sender_phone}` : ""}
              </p>
              {r.return_status === "return_requested" && (
                <div className="mt-3 flex items-center justify-between gap-2">
                  <Countdown deadline={r.return_accept_deadline} />
                  <Button
                    size="sm"
                    disabled={busy === r.package_id}
                    onClick={() => accept(r.package_id)}
                    data-testid={`accept-return-${r.package_id}`}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {busy === r.package_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept return"}
                  </Button>
                </div>
              )}
              {r.return_status === "return_accepted" && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-amber-200 inline-flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Accepted — enter the sender's return PIN to finish
                  </p>
                  <div className="flex gap-2">
                    <input
                      inputMode="numeric"
                      value={pins[r.package_id] || ""}
                      onChange={(e) => setPins((p) => ({ ...p, [r.package_id]: e.target.value.replace(/\D/g, "") }))}
                      placeholder="Return PIN"
                      data-testid={`return-pin-input-${r.package_id}`}
                      className="flex-1 rounded-lg bg-slate-900 border border-slate-600 px-3 py-2 text-white text-sm tracking-widest"
                    />
                    <Button
                      size="sm"
                      disabled={busy === r.package_id}
                      onClick={() => complete(r.package_id)}
                      data-testid={`complete-return-${r.package_id}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {busy === r.package_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete return"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DriverReturnTasks;
